import React from "react"

import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { useTrialBlock } from "@/contexts/TrialBlockContext"
import { useDepots } from "@/hooks/useDepots"
import { cn } from "@/lib/utils"

const TrialBlockFilters: React.FC = () => {
  // Fetch depots from Supabase
  const { data: depots, isLoading: depotsLoading } = useDepots()

  const {
    selectedLocation,
    setSelectedLocation,
    showPendingOnly,
    setShowPendingOnly,
    driverNameSearch,
    setDriverNameSearch,
    selectedDate,
    setSelectedDate,
  } = useTrialBlock()

  return (
    <div className="bg-palette-jungleGreen000 p-3 rounded-md mb-6">
      <div className="grid gap-4 md:grid-cols-4">
        {/* Location Filter */}
        <div className="space-y-2">
          <Label htmlFor="location" className="text-sm font-medium">
            Filter by Location
          </Label>
          <Select value={selectedLocation} onValueChange={setSelectedLocation}>
            <SelectTrigger id="location" className="h-10">
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {depotsLoading ? (
                <SelectItem value="loading" disabled>
                  Loading depots...
                </SelectItem>
              ) : (
                depots.map((depot) => (
                  <SelectItem key={depot.id} value={depot.id}>
                    {depot.name} ({depot.city})
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Applicant Name Search */}
        <div className="space-y-2">
          <Label htmlFor="driverName" className="text-sm font-medium">
            Applicant Name
          </Label>
          <Input
            id="driverName"
            placeholder="Search by name"
            value={driverNameSearch}
            onChange={(e) => setDriverNameSearch(e.target.value)}
            className="h-10"
          />
        </div>

        {/* Date Filter - Changed to specific date */}
        <div className="space-y-2">
          <Label htmlFor="date" className="text-sm font-medium">
            Date
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal h-10",
                  !selectedDate && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? format(selectedDate, "PPP") : <span>Select date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Show Pending Only Toggle */}
        <div className="space-y-2">
          <Label htmlFor="pending-only" className="text-sm font-medium">
            Show Pending Only
          </Label>
          <div className="flex items-center h-10">
            <Switch
              id="pending-only"
              checked={showPendingOnly}
              onCheckedChange={setShowPendingOnly}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default TrialBlockFilters
